import * as React from "react";

import {
  getDeals,
  SUPER_DEALS_UPDATED_EVENT,
  DealCard,
  updateDeal,
  addCustomDeal,
  deleteDeal,
} from "./DealCard";


const MAX_IMAGE_SIZE = 100 * 1024; // 100 KB
const DEFAULT_WA_NUMBER = "918438506813";

const PLACEHOLDER_IMAGE =
  "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='800' height='500' viewBox='0 0 800 500'%3E%3Cdefs%3E%3ClinearGradient id='g' x1='0' y1='0' x2='1' y2='1'%3E%3Cstop offset='0%25' stop-color='%23f97316'/%3E%3Cstop offset='100%25' stop-color='%23ef4444'/%3E%3C/linearGradient%3E%3C/defs%3E%3Crect width='800' height='500' fill='url(%23g)'/%3E%3Ctext x='50%25' y='48%25' dominant-baseline='middle' text-anchor='middle' fill='white' font-size='44' font-family='Arial'%3ESuper Deals%3C/text%3E%3Ctext x='50%25' y='58%25' dominant-baseline='middle' text-anchor='middle' fill='white' font-size='24' font-family='Arial'%3EImage Preview%3C/text%3E%3C/svg%3E";

const EMPTY_FORM = {
  category: "Indian Escapes Deals",
  countryCity: "",
  title: "",
  badgeText: "LIMITED TIME",
  liveTagText: "LIVE NOW",
  offerText: "",
  priceTagText: "",
  validUntil: "",
  audienceText: "100+ Customer",
  partner: "",
  airlinesPlatform: "",
  image: "",
  imageFileName: "",
  coupon: "",
  discount: "",
  link: "",
  sourceLink: "",
  displayTitle: "",
  displayDescription: "",
  description: "",
  cta: "",
  primaryCta: "Today's Deal",
  secondaryCta: "Check Out",
  whatsAppNumber: DEFAULT_WA_NUMBER,
  whatsAppMessage: "",
};

const CREATE_NEW_TAG = "__create_new_tag__";

const TOAST_STYLES = {
  success: "border-[#bbf7d0] bg-[#f0fdf4] text-[#166534]",
  error: "border-[#fecaca] bg-[#fff5f5] text-[#991b1b]",
  info: "border-[#bfdbfe] bg-[#eff6ff] text-[#1d4ed8]",
};

const TOAST_LABELS = {
  success: "Success",
  error: "Error",
  info: "Info",
};


function getTimestamp() {
  const now = new Date();
  const date = now.toISOString().slice(0, 10);
  const time = now.toTimeString().slice(0, 8).replace(/:/g, "-");
  return `${date}_${time}`;
}

function downloadBackupJS(filename, varName, data) {
  if (typeof window === "undefined") return;
  const content = `// Travel Rethink Ways backup\n// Generated at: ${new Date().toISOString()}\nwindow.${varName} = ${JSON.stringify(data, null, 2)};\n`;
  const blob = new Blob([content], { type: "text/javascript;charset=utf-8" });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${filename}-${getTimestamp()}.js`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.URL.revokeObjectURL(url);
}

function getDefaultWhatsAppMessage(title, category) {
  const isInternational =
    category !== "Indian Escapes Deals" &&
    category !== "Attractions" &&
    category !== "Hotels" &&
    category !== "Flights";
  const section = category === "Attractions"
    ? "Attractions section"
    : category === "Hotels"
      ? "Hotels section"
      : isInternational
        ? "International section"
        : "India section";
  return `Hi Travel Rethink Ways,\n\nI came across your "${title || "deal"}" offer under ${section}.\n\nCould you please share more details?`;
}

function buildWhatsAppLink(message, number = DEFAULT_WA_NUMBER) {
  const normalizedNumber = String(number || "").replace(/\D/g, "") || DEFAULT_WA_NUMBER;
  const text = String(message || "").trim();
  const base = `https://wa.me/${normalizedNumber}`;
  return text ? `${base}?text=${encodeURIComponent(text)}` : base;
}

function extractWhatsAppMessage(value, fallback = "") {
  const raw = String(value || "").trim();
  if (!raw) return fallback;
  try {
    const parsed = new URL(raw);
    return parsed.searchParams.get("text")?.trim() || fallback;
  } catch {
    const [, textPart = ""] = raw.split("?text=");
    if (!textPart) return fallback;
    try {
      return decodeURIComponent(textPart).trim() || fallback;
    } catch {
      return textPart.trim() || fallback;
    }
  }
}

function extractWhatsAppNumber(value, fallback = DEFAULT_WA_NUMBER) {
  const raw = String(value || "").trim();
  if (!raw) return fallback;
  const match = raw.match(/wa\.me\/(\d+)/i);
  return match?.[1] || fallback;
}


export function SuperDealsManager() {
  const [form, setForm] = React.useState(EMPTY_FORM);
  const [deals, setDeals] = React.useState(() => getDeals());
  const [editingId, setEditingId] = React.useState(null);
  const [filterCategory, setFilterCategory] = React.useState("all");
  const [searchQuery, setSearchQuery] = React.useState("");
  const [toast, setToast] = React.useState(null);
  const [imageInputKey, setImageInputKey] = React.useState(0);
  const [selectedCategory, setSelectedCategory] = React.useState(EMPTY_FORM.category);
  const [newTagInput, setNewTagInput] = React.useState("");

  function showToast(type, text) {
    setToast({ type, text });
  }

  React.useEffect(() => {
    if (!toast) return;
    const timer = window.setTimeout(() => setToast(null), 2600);
    return () => window.clearTimeout(timer);
  }, [toast]);

  React.useEffect(() => {
    const refresh = () => setDeals(getDeals());
    window.addEventListener(SUPER_DEALS_UPDATED_EVENT, refresh);
    window.addEventListener("storage", refresh);
    window.addEventListener("pageshow", refresh);
    window.addEventListener("focus", refresh);
    return () => {
      window.removeEventListener(SUPER_DEALS_UPDATED_EVENT, refresh);
      window.removeEventListener("storage", refresh);
      window.removeEventListener("pageshow", refresh);
      window.removeEventListener("focus", refresh);
    };
  }, []);

  const categories = React.useMemo(() => {
    const set = new Set(deals.map((d) => d.category).filter(Boolean));
    if (form.category.trim()) set.add(form.category.trim());
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [form.category, deals]);

  const filterTabs = React.useMemo(
    () => [{ code: "all", label: "all" }, ...categories.map((c) => ({ code: c, label: c }))],
    [categories]
  );

  const filteredDeals = React.useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    return deals.filter((d) => {
      const matchesCategory = filterCategory === "all" || d.category === filterCategory;
      if (!matchesCategory) return false;
      if (!query) return true;
      return [d.category, d.title, d.displayTitle, d.displayDescription, d.description]
        .join(" ")
        .toLowerCase()
        .includes(query);
    });
  }, [filterCategory, deals, searchQuery]);

  const previewDeal = React.useMemo(
    () => ({
      id: "preview-super-deal",
      category: form.category.trim() || "General Deals",
      countryCity: form.displayDescription.trim(),
      title: form.displayTitle.trim() || "Deal Title",
      badgeText: form.badgeText.trim() || "LIMITED TIME",
      liveTagText: form.liveTagText.trim() || "LIVE NOW",
      offerText: form.offerText.trim() || form.description.trim() || "Upto Rs5,000 OFF",
      priceTagText: "",
      validUntil: form.validUntil.trim() || "31TH MAY 2026",
      audienceText: form.audienceText.trim() || "100+ Travelers Booked",
      partner: "",
      airlinesPlatform: "",
      image: form.image.trim() || deals[0]?.image || PLACEHOLDER_IMAGE,
      coupon: "",
      discount: "",
      link: form.link.trim() || "#",
      sourceLink: "",
      displayTitle: form.displayTitle.trim() || "Deal Title",
      displayDescription:
        form.displayDescription.trim() || "Port Blair | Havelock | Neil Island",
      description: form.description.trim() || "Cellular Jail Tour | Radhanagar Beach | Elephant Beach",
      cta: form.secondaryCta.trim() || "Check Out",
      primaryCta: form.primaryCta.trim() || "Today's Deal",
      secondaryCta: form.secondaryCta.trim() || "Check Out",
      whatsAppNumber: form.whatsAppNumber.trim() || DEFAULT_WA_NUMBER,
      whatsAppMessage:
        form.whatsAppMessage.trim() ||
        getDefaultWhatsAppMessage(form.displayTitle.trim() || "Deal Title", form.category.trim() || "General Deals"),
    }),
    [form, deals]
  );

  function handleFieldChange(e) {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  }

  function handleCategoryChange(e) {
    const value = e.target.value;
    setSelectedCategory(value);
    if (value === CREATE_NEW_TAG) {
      setNewTagInput("");
      setForm((prev) => ({ ...prev, category: "" }));
      showToast("info", "Enter a new tag and click Create Tag.");
      return;
    }
    setNewTagInput("");
    setForm((prev) => ({ ...prev, category: value }));
  }

  function handleCreateTag() {
    const tag = newTagInput.trim();
    if (!tag) {
      showToast("error", "Enter a tag name to create.");
      return;
    }
    setForm((prev) => ({ ...prev, category: tag }));
    setSelectedCategory(tag);
    setNewTagInput("");
    showToast("success", `Tag created: ${tag}`);
  }

  function handleImageUpload(e) {
    const file = e.target.files?.[0];
    if (!file) return;

    const isAvifMime = file.type === "image/avif";
    const isAvifExt = /\.avif$/i.test(file.name);
    if (!isAvifMime && !isAvifExt) {
      showToast("error", "Only AVIF files are allowed.");
      e.target.value = "";
      return;
    }
    if (file.size > MAX_IMAGE_SIZE) {
      showToast("error", "Image size must be 100KB or less.");
      e.target.value = "";
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      setForm((prev) => ({
        ...prev,
        image: String(reader.result || ""),
        imageFileName: file.name,
      }));
      showToast("success", `Image uploaded: ${file.name}`);
    };
    reader.onerror = () => {
      showToast("error", "Unable to read image file. Please try another AVIF image.");
    };
    reader.readAsDataURL(file);
  }

  function handleSubmit(e) {
    e.preventDefault();

    const category = form.category.trim();
    const displayTitle = form.displayTitle.trim();
    const displayDescription = form.displayDescription.trim() || "Port Blair | Havelock | Neil Island";
    const offerText = form.offerText.trim() || form.description.trim();
    const whatsAppMessage =
      form.whatsAppMessage.trim() ||
      getDefaultWhatsAppMessage(displayTitle, category);
    const whatsAppNumber = form.whatsAppNumber.trim() || DEFAULT_WA_NUMBER;

    if (!category) { showToast("error", "Category is required."); return; }
    if (!displayTitle) { showToast("error", "Title is required."); return; }
    if (!offerText) { showToast("error", "Offer text is required."); return; }
    if (!form.link.trim()) { showToast("error", "Primary deal link is required."); return; }
    if (!form.image.trim()) {
      showToast("error", "Upload one AVIF image (max 100KB), or keep the existing image.");
      return;
    }

    const payload = {
      category,
      countryCity: displayDescription,
      title: displayTitle,
      badgeText: form.badgeText.trim(),
      liveTagText: form.liveTagText.trim(),
      offerText,
      priceTagText: "",
      validUntil: form.validUntil.trim(),
      audienceText: form.audienceText.trim(),
      partner: "",
      airlinesPlatform: "",
      image: form.image.trim(),
      imageFileName: form.imageFileName.trim(),
      coupon: "",
      discount: "",
      link: form.link.trim(),
      sourceLink: "",
      displayTitle,
      displayDescription,
      description: form.description.trim() || offerText,
      cta: form.secondaryCta.trim() || "Check Out",
      primaryCta: form.primaryCta.trim(),
      secondaryCta: form.secondaryCta.trim() || "Check Out",
      whatsAppNumber,
      whatsAppMessage,
      whatsAppLink: buildWhatsAppLink(whatsAppMessage, whatsAppNumber),
    };

    if (editingId) {
      setDeals(updateDeal(editingId, payload));
      showToast("success", "Super Deal updated.");
    } else {
      setDeals(addCustomDeal(payload));
      showToast("success", "Super Deal saved. It will appear on the Super Deals page immediately.");
    }

    setForm((prev) => ({ ...EMPTY_FORM, category: prev.category.trim() || EMPTY_FORM.category }));
    setSelectedCategory(form.category.trim() || EMPTY_FORM.category);
    setNewTagInput("");
    setEditingId(null);
    setImageInputKey((k) => k + 1);
  }

  function handleEdit(deal) {
    setEditingId(String(deal.id));
    setForm({
      category: deal.category || "",
      countryCity: deal.displayDescription || deal.countryCity || "",
      title: deal.displayTitle || deal.title || "",
      badgeText: deal.badgeText || "LIMITED TIME",
      liveTagText: deal.liveTagText || "LIVE NOW",
      offerText: deal.offerText || deal.description || "",
      priceTagText: "",
      validUntil: deal.validUntil || "",
      audienceText: deal.audienceText || "100+ Travelers Booked",
      partner: "",
      airlinesPlatform: "",
      image: deal.image || "",
      imageFileName: deal.imageFileName || "",
      coupon: "",
      discount: "",
      link: deal.link || "",
      sourceLink: "",
      displayTitle: deal.displayTitle || "",
      displayDescription: deal.displayDescription || "",
      description: deal.description || "",
      cta: deal.secondaryCta || deal.cta || "Check Out",
      primaryCta: deal.primaryCta || "Today's Deal",
      secondaryCta: deal.secondaryCta || deal.cta || "Check Out",
      whatsAppNumber: deal.whatsAppNumber || extractWhatsAppNumber(deal.whatsAppLink, DEFAULT_WA_NUMBER),
      whatsAppMessage:
        deal.whatsAppMessage ||
        extractWhatsAppMessage(
          deal.whatsAppLink,
          getDefaultWhatsAppMessage(deal.displayTitle || deal.title || "deal", deal.category || "General Deals")
        ),
    });
    setSelectedCategory(deal.category || CREATE_NEW_TAG);
    setNewTagInput("");
    setImageInputKey((k) => k + 1);
    showToast("info", `Editing deal: ${deal.displayTitle || deal.title}`);
  }

  function handleDelete(id) {
    if (!window.confirm("Are you sure you want to delete this saved deal?")) return;
    const updated = deleteDeal(id);
    setDeals(updated);
    if (editingId === String(id)) {
      setEditingId(null);
      setForm(EMPTY_FORM);
      setSelectedCategory(EMPTY_FORM.category);
      setNewTagInput("");
      setImageInputKey((k) => k + 1);
    }
    showToast("success", "Saved Super Deal deleted.");
  }

  function handleCancelEdit() {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setSelectedCategory(EMPTY_FORM.category);
    setNewTagInput("");
    setImageInputKey((k) => k + 1);
    showToast("info", "Edit cancelled.");
  }

  function handleDownloadBackup() {
    const allDeals = getDeals();
    const data = {
      generatedAt: new Date().toISOString(),
      source: "super-deals-form",
      totalDeals: allDeals.length,
      deals: allDeals,
    };
    downloadBackupJS("super-deals-backup", "TRW_SUPER_DEALS_BACKUP", data);
    showToast("success", "Backup JS downloaded for Super Deals.");
  }


  const inputCls =
    "mt-2 w-full rounded-xl border border-[#d1d5db] px-4 py-3 text-sm outline-none focus:border-[#111827]";

  const labelCls = "text-sm font-semibold text-[#1f2937]";


  return (
    <div className="mx-auto w-full max-w-[1500px] px-4 py-8">
      <div className="grid grid-cols-1 items-start gap-7 lg:grid-cols-[minmax(0,1.25fr)_minmax(0,1fr)]">

        <section className="rounded-[20px] border border-[#d9d9d9] bg-white p-6 shadow-[0_20px_55px_rgba(0,0,0,0.07)]">
          <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
            <h2 className="text-[24px] font-semibold tracking-[-0.2px] text-black">
              Super Deals Manager
            </h2>
            <button
              type="button"
              onClick={handleDownloadBackup}
              className="rounded-full border border-black px-5 py-2 text-xs font-semibold tracking-[0.35px] uppercase text-black transition-colors hover:bg-black hover:text-white"
            >
              Download JS Backup
            </button>
          </div>

          <p className="mb-5 text-sm text-[#4b5563]">
            Enter only the fields shown on the live Super Deal card.
          </p>

          <form onSubmit={handleSubmit} className="space-y-5">

            {/* Category + title */}
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <label className={labelCls}>
                Category
                <select
                  value={selectedCategory}
                  onChange={handleCategoryChange}
                  className={inputCls}
                >
                  {categories.map((cat) => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                  <option value={CREATE_NEW_TAG}>+ Create New Tag</option>
                </select>
              </label>

              <label className={labelCls}>
                Card Title
                <input
                  type="text"
                  name="displayTitle"
                  value={form.displayTitle}
                  onChange={handleFieldChange}
                  placeholder="Andaman - Coral Islands"
                  className={inputCls}
                />
              </label>
            </div>

            {/* New tag input */}
            {selectedCategory === CREATE_NEW_TAG && (
              <div className="grid grid-cols-1 gap-3 md:grid-cols-[minmax(0,1fr)_auto] md:items-end">
                <label className={labelCls}>
                  New Tag Name
                  <input
                    type="text"
                    value={newTagInput}
                    onChange={(e) => setNewTagInput(e.target.value)}
                    placeholder="Example: Weekend Flash Deals"
                    className={inputCls}
                  />
                </label>
                <button
                  type="button"
                  onClick={handleCreateTag}
                  className="rounded-full border border-black px-5 py-3 text-xs font-semibold tracking-[0.35px] uppercase text-black transition-colors hover:bg-black hover:text-white"
                >
                  Create Tag
                </button>
              </div>
            )}

            {/* Badge + Live tag */}
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <label className={labelCls}>
                Top Badge On Image
                <input
                  type="text"
                  name="badgeText"
                  value={form.badgeText}
                  onChange={handleFieldChange}
                  placeholder="LIMITED TIME"
                  className={inputCls}
                />
              </label>
              <label className={labelCls}>
                Live Tag On Image
                <input
                  type="text"
                  name="liveTagText"
                  value={form.liveTagText}
                  onChange={handleFieldChange}
                  placeholder="LIVE NOW"
                  className={inputCls}
                />
              </label>
            </div>

            {/* Offer text */}
            <label className={`block ${labelCls}`}>
              Main Offer Text On Image
              <input
                type="text"
                name="offerText"
                value={form.offerText}
                onChange={handleFieldChange}
                placeholder="Upto Rs 5,000 OFF"
                className={inputCls}
              />
            </label>

            <label className={`block ${labelCls}`}>
              Extra Details Line
              <textarea
                name="description"
                value={form.description}
                onChange={handleFieldChange}
                rows={2}
                placeholder="Cellular Jail Tour | Radhanagar Beach | Elephant Beach"
                className={inputCls}
              />
            </label>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <label className={labelCls}>
                Valid Till
                <input
                  type="text"
                  name="validUntil"
                  value={form.validUntil}
                  onChange={handleFieldChange}
                  placeholder="31TH MAY 2026"
                  className={inputCls}
                />
              </label>
              <label className={labelCls}>
                Audience Text
                <input
                  type="text"
                  name="audienceText"
                  value={form.audienceText}
                  onChange={handleFieldChange}
                  placeholder="100+ Travelers Booked"
                  className={inputCls}
                />
              </label>
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <label className={labelCls}>
                Primary CTA Button
                <input
                  type="text"
                  name="primaryCta"
                  value={form.primaryCta}
                  onChange={handleFieldChange}
                  placeholder="Today's Deal"
                  className={inputCls}
                />
              </label>
              <label className={labelCls}>
                Secondary CTA Button
                <input
                  type="text"
                  name="secondaryCta"
                  value={form.secondaryCta}
                  onChange={handleFieldChange}
                  placeholder="Check Out"
                  className={inputCls}
                />
              </label>
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <label className={labelCls}>
                WhatsApp Number
                <input
                  type="text"
                  name="whatsAppNumber"
                  value={form.whatsAppNumber}
                  onChange={handleFieldChange}
                  placeholder="918438506813"
                  className={inputCls}
                />
              </label>
              <label className={labelCls}>
                WhatsApp Message
                <textarea
                  name="whatsAppMessage"
                  value={form.whatsAppMessage}
                  onChange={handleFieldChange}
                  rows={4}
                  placeholder={`Hi Travel Rethink Ways,\n\nI came across your "Andaman - Coral Islands" offer.\n\nCould you please share more details?`}
                  className={inputCls}
                />
              </label>
            </div>

            <div className="grid grid-cols-1 gap-4">
              <label className={labelCls}>
                Primary Deal Link
                <input
                  type="url"
                  name="link"
                  value={form.link}
                  onChange={handleFieldChange}
                  placeholder="https://travel.rethinkways.com/..."
                  className={inputCls}
                />
              </label>
            </div>
            {/* Image upload */}
            <div className="rounded-2xl border border-[#d1d5db] p-4">
              <p className="text-sm font-semibold text-[#1f2937]">Card Image</p>

              <label className="mt-3 block text-sm font-semibold text-[#1f2937]">
                Upload AVIF File
                <input
                  key={imageInputKey}
                  type="file"
                  accept=".avif,image/avif"
                  onChange={handleImageUpload}
                  className="mt-2 block w-full cursor-pointer rounded-xl border border-[#d1d5db] bg-white px-4 py-3 text-sm text-[#111827] file:mr-4 file:rounded-lg file:border-0 file:bg-[#111827] file:px-3 file:py-2 file:text-xs file:font-semibold file:text-white hover:file:bg-[#e01919]"
                />
              </label>

              <p className="mt-2 text-xs text-[#6b7280]">
                Allowed: <span className="font-semibold">AVIF only</span>, max size{" "}
                <span className="font-semibold">100KB</span>.
              </p>

              {form.imageFileName && (
                <p className="mt-2 text-xs font-semibold text-[#065f46]">
                  Selected file: {form.imageFileName}
                </p>
              )}

              {form.image && !form.imageFileName && (
                <p className="mt-2 text-xs text-[#6b7280]">
                  Using the current saved image. Upload a new AVIF only if you want to replace it.
                </p>
              )}
            </div>

            {/* Submit / Cancel */}
            <div className="flex flex-wrap items-center gap-3">
              <button
                type="submit"
                className="rounded-full bg-[#e01919] px-6 py-3 text-xs font-semibold tracking-[0.4px] uppercase text-white transition-colors hover:bg-black"
              >
                {editingId ? "Update Super Deal" : "Save Super Deal"}
              </button>
              {editingId && (
                <button
                  type="button"
                  onClick={handleCancelEdit}
                  className="rounded-full border border-black px-6 py-3 text-xs font-semibold tracking-[0.4px] uppercase text-black transition-colors hover:bg-black hover:text-white"
                >
                  Cancel Edit
                </button>
              )}
            </div>
          </form>
        </section>

        <section className="space-y-5">
          <div className="rounded-[20px] border border-[#d9d9d9] bg-white p-6 shadow-[0_20px_55px_rgba(0,0,0,0.07)]">
            <h3 className="text-[20px] font-semibold tracking-[-0.2px] text-black">
              Live Preview
            </h3>
            <p className="mt-2 text-sm text-[#4b5563]">
              This preview matches the visible Super Deal card fields.
            </p>
            <div className="mt-5">
              <DealCard deal={previewDeal} />
            </div>
          </div>
        </section>
      </div>

      <section className="mt-8 rounded-[20px] border border-[#d9d9d9] bg-white p-6 shadow-[0_20px_55px_rgba(0,0,0,0.07)]">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h3 className="text-[20px] font-semibold tracking-[-0.2px] text-black">
            Available Super Deals ({filteredDeals.length})
          </h3>
          <input
            type="search"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search title or location..."
            className="w-full max-w-[320px] rounded-xl border border-[#d1d5db] px-4 py-3 text-sm outline-none focus:border-[#111827]"
          />
        </div>

        <div className="mt-4 flex flex-wrap gap-2">
          {filterTabs.map((tab) => {
            const active = filterCategory === tab.code;
            return (
              <button
                key={tab.code}
                type="button"
                onClick={() => setFilterCategory(tab.code)}
                className={`rounded-full border px-4 py-2 text-xs font-semibold uppercase tracking-[0.3px] transition-colors ${
                  active
                    ? "border-black bg-black text-white"
                    : "border-[#d1d5db] bg-white text-black hover:border-black"
                }`}
              >
                {tab.label}
              </button>
            );
          })}
        </div>

        {filteredDeals.length === 0 ? (
          <p className="mt-5 rounded-xl border border-dashed border-[#d1d5db] px-4 py-6 text-sm text-[#6b7280]">
            No deals match the current search or category filter.
          </p>
        ) : (
          <ul className="mt-5 space-y-3">
            {filteredDeals.map((deal) => (
              <li
                key={deal.id}
                className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-[#e5e7eb] bg-[#f9fafb] px-4 py-3"
              >
                <div>
                  <p className="text-sm font-semibold text-black">
                    {deal.displayTitle || deal.title}
                  </p>
                  <p className="text-xs text-[#6b7280]">{deal.category}</p>
                  {deal.countryCity && (
                    <p className="text-xs text-[#6b7280]">{deal.countryCity}</p>
                  )}
                  <p className="text-xs text-[#6b7280]">
                    Valid Till: {deal.validUntil || "-"} | Audience: {deal.audienceText || "-"}
                  </p>
                  <p className="text-xs text-[#6b7280]">
                    {String(deal.id).startsWith("custom-") ? "Custom deal" : "Default deal"}
                  </p>
                  {deal.imageFileName && (
                    <p className="text-xs text-[#6b7280]">{deal.imageFileName}</p>
                  )}
                </div>

                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => handleEdit(deal)}
                    className="rounded-full border border-black px-3 py-1 text-[10px] font-semibold tracking-[0.4px] uppercase text-black transition-colors hover:bg-black hover:text-white"
                  >
                    Edit
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDelete(deal.id)}
                    className="rounded-full border border-[#dc2626] px-3 py-1 text-[10px] font-semibold tracking-[0.4px] uppercase text-[#dc2626] transition-colors hover:bg-[#dc2626] hover:text-white"
                  >
                    Delete
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      {toast && (
        <div className="fixed inset-0 z-[100000] flex items-center justify-center bg-black/45 p-4">
          <div
            role="alert"
            aria-live="assertive"
            className={`w-full max-w-[420px] rounded-2xl border px-5 py-4 shadow-[0_24px_60px_rgba(0,0,0,0.3)] ${
              TOAST_STYLES[toast.type] || TOAST_STYLES.info
            }`}
          >
            <p className="text-[11px] font-semibold tracking-[0.35px] uppercase">
              {TOAST_LABELS[toast.type] || TOAST_LABELS.info}
            </p>
            <p className="mt-2 text-sm font-semibold">{toast.text}</p>
            <div className="mt-4 flex justify-end">
              <button
                type="button"
                onClick={() => setToast(null)}
                className="rounded-full border border-current px-4 py-1.5 text-[11px] font-semibold tracking-[0.3px] uppercase"
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Named export matching original: export { SuperDealsManager as S }
export { SuperDealsManager as S };

export default SuperDealsManager;


